import { useMutation, useQuery } from "react-query";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import reject from "lodash/reject";

import { gwtClient } from "@monta/utils";

import type { QueryKey } from "react-query";
import type { GetMainExamResultset, UpsertExamResultComment } from "./types";

/**
 * Service for fetching main exam resultset
 * Uses POST method with form-encoded data as specified in requirements
 */
export const getMainExamResultset = {
  url: () => `/exam/getMainExamResultset`,
  fn: async (request: GetMainExamResultset["request"]) => {
    const { data } = await gwtClient<GetMainExamResultset["response"]>({
      ...request?.axiosConfig,
      method: "POST",
      url: getMainExamResultset.url(),
      data: request.body,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...request?.axiosConfig?.headers
      }
    });
    return data;
  },
  key: (request: GetMainExamResultset["request"]) => {
    return reject(
      [getMainExamResultset.url(), omit(request ?? {}, "axiosConfig")],
      isEmpty
    ) as QueryKey;
  },
  query: (options: GetMainExamResultset["queryOptions"]) => ({
    ...options.queryOptions,
    queryKey: getMainExamResultset.key(options.request),
    queryFn: () => getMainExamResultset.fn(options.request)
  }),
  useQuery: (options: GetMainExamResultset["queryOptions"]) =>
    useQuery(getMainExamResultset.query(options)),
  useMutation: (options?: GetMainExamResultset["mutationOptions"]) =>
    useMutation({
      ...options,
      mutationFn: getMainExamResultset.fn
    })
};

export const examResultCommentInsertOrUpdate = {
  url: () => `/exam/examResultCommentInsertOrUpdate`,
  fn: async (request: UpsertExamResultComment["request"]) => {
    const { data } = await gwtClient<UpsertExamResultComment["response"]>({
      ...request?.axiosConfig,
      method: "POST",
      url: examResultCommentInsertOrUpdate.url(),
      data: request.body,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...request?.axiosConfig?.headers
      }
    });
    return data;
  },
  key: (request: UpsertExamResultComment["request"]) => {
    return reject(
      [
        examResultCommentInsertOrUpdate.url(),
        omit(request ?? {}, "axiosConfig")
      ],
      isEmpty
    ) as QueryKey;
  },
  query: (options: UpsertExamResultComment["queryOptions"]) => ({
    ...options.queryOptions,
    queryKey: examResultCommentInsertOrUpdate.key(options.request),
    queryFn: () => examResultCommentInsertOrUpdate.fn(options.request)
  }),
  useQuery: (options: UpsertExamResultComment["queryOptions"]) =>
    useQuery(examResultCommentInsertOrUpdate.query(options)),
  useMutation: (options?: UpsertExamResultComment["mutationOptions"]) =>
    useMutation({
      ...options,
      mutationFn: examResultCommentInsertOrUpdate.fn
    })
};
